import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { useForm, FormProvider } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/entities/Comprador/steps'
import type { TClienteCompleto } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/hooks/use-stepper-proposta'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { StepCompradorLayout } from '@/pages/proposta/etapa-comprador/cadastro-comprador/components/stepper-comprador'
import { ContatosComprador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosCompradorPF } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-comprador-pf'
import { DadosConjuge } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-conjuge'
import { DadosMaePai } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-mae-pai'
import { EstadoCivilComprador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/estado-civil-comprador'
import { EstadoCivilVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { VincularCompradorMenor } from '@/pages/proposta/etapa-comprador/cadastro-comprador/vincular-comprador-menor'
import { origemTelefone } from '@/shared/interfaces/cliente.enums'
import type { TNacionalidade, TProfissao } from '@/shared/interfaces/cliente.types'

type TCadastroCompradorPF = {
  mode: CompradorPessoaFisicaStepSchemaOutput['mode']
  clienteAtendimento: TPropostaSchemaInput['informacoesIniciais']['clienteAtendimento']
  compradores: TCompradoresCadastrados
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  onCancel: () => void
  onSubmit: (data: CompradorPessoaFisicaStepSchemaOutput, isDirty: boolean) => Promise<void>
  nacionalidades: TNacionalidade[]
  clienteToEdit: TClienteCompleto | null
  profissoes: TProfissao[]
  emailsJaVinculados: Record<string, string>
}

export function CadastroCompradorPF({
  mode,
  onCancel,
  onSubmit,
  clienteAtendimento,
  clienteToEdit,
  clientes,
  compradores,
  nacionalidades,
  profissoes,
  emailsJaVinculados,
}: Readonly<TCadastroCompradorPF>) {
  const { schema, conditionalSteps } = stepsFlow.compradorPessoaFisica
  const form = useForm<CompradorPessoaFisicaStepSchemaInput, unknown, CompradorPessoaFisicaStepSchemaOutput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      mode,
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        buscouCpf: false,
        cpf: '',
        nacionalidadeId: nacionalidades[0] ? nacionalidades[0].id : '',
        profissaoId: '',
        email: '',
        id: '',
        maiorDe18Anos: '',
        nome: '',
      },
      estadoCivil: {
        status: undefined,
        tipoAssinatura: null,
        regimeCasamento: null,
        uniaoEstavel: null,
      },
      imagem: null,
      tipoDoCliente: 'Contato',
      tipo: 'PessoaFisica',
      conjuge: null,
      emailsJaVinculados,
    },
  })

  const isDirty = form.formState.isDirty
  const { steps, idx, onNext, onFinish, onBack, isLast } = useStepManager(
    conditionalSteps,
    () => form.watch(),
    onCancel,
    onSubmit,
    isDirty,
  )

  const currentStep = steps[idx]

  const handleOnFinish = async (
    output: CompradorPessoaFisicaStepSchemaOutput | CompradorPessoaJuridicaSchemaOutput,
  ) => {
    await onFinish(output as CompradorPessoaFisicaStepSchemaOutput)
  }

  useEffect(() => {
    if (clienteToEdit) {
      const isMenor = clienteToEdit.maiorDe18Anos === 'nao'
      form.reset({
        emailsJaVinculados,
        fluxoIndependente: clienteToEdit.vinculo?.tipo === 'ConjugeFluxoIndependente' ? 'Conjuge' : null,
        contato: {
          email: clienteToEdit.email,
          telefone: {
            origem: clienteToEdit.telefone.origem,
            numero: clienteToEdit.telefone.numero,
            principal: true,
          },
        },
        dados: {
          buscouCpf: true,
          cpf: clienteToEdit.documento,
          nacionalidadeId: clienteToEdit.nacionalidadeId,
          profissaoId: clienteToEdit.profissaoId,
          maiorDe18Anos: clienteToEdit.maiorDe18Anos ?? 'nao',
          id: clienteToEdit.id,
          nome: clienteToEdit.nome,
          email: isMenor ? clienteToEdit.email : null,
        },
        estadoCivil: {
          status: clienteToEdit.estadoCivil,
          tipoAssinatura: clienteToEdit.tipoAssinatura,
          regimeCasamento: clienteToEdit.regimeCasamento,
          uniaoEstavel: clienteToEdit.uniaoEstavel,
        },
        mode: 'edit',
        imagem: clienteToEdit.imagem ?? null,
        tipoDoCliente: 'Contato',
        tipo: 'PessoaFisica',
        conjuge: clienteToEdit.vinculo
          ? {
              imagem: clienteToEdit.vinculo.imagem,
              tipo: 'Conjuge',
              dados: {
                id: clienteToEdit.vinculo.id,
                buscouCpf: true,
                cpf: clienteToEdit.vinculo.documento,
                nacionalidadeId: clienteToEdit.vinculo.nacionalidadeId,
                profissaoId: clienteToEdit.vinculo.profissaoId,
                maiorDe18Anos: clienteToEdit.maiorDe18Anos ?? 'nao',
                nome: clienteToEdit.vinculo.nome,
                email: null,
              },
              contato: {
                email: clienteToEdit.vinculo.email,
                telefone: {
                  origem: clienteToEdit.vinculo.telefone.origem,
                  numero: clienteToEdit.vinculo.telefone.numero,
                  principal: true,
                },
              },
            }
          : null,
      })
    }
  }, [clienteToEdit, emailsJaVinculados, form])

  const renderContent = () => {
    switch (currentStep) {
      case 'vincularCompradorMenor':
        return (
          <VincularCompradorMenor
            emailsJaVinculados={emailsJaVinculados}
            onBack={onBack}
            onNext={onNext}
            clientes={clientes}
            compradores={compradores}
          />
        )
      case 'dadosPF':
        return (
          <DadosCompradorPF
            nacionalidades={nacionalidades}
            profissoes={profissoes}
            onBack={onBack}
            isLast={isLast}
            onFinish={onFinish}
            clienteAtendimento={clienteAtendimento}
            compradores={compradores}
            onNext={onNext}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'contatosPF':
        return (
          <ContatosComprador
            onNext={onNext}
            onBack={onBack}
            titulo={t('proposta:cadastro_compradores.steps.contatos.titulo')}
            fieldName="contato"
            isLast={false}
            onFinish={(e) => handleOnFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
          />
        )
      case 'estadoCivil':
        return (
          <EstadoCivilComprador
            onNext={onNext}
            onBack={onBack}
            onFinish={onFinish}
            isLast={isLast}
            nacionalidadePrincipalId={nacionalidades[0]?.id}
          />
        )
      case 'dadosConjuge':
        return (
          <DadosConjuge
            onNext={onNext}
            onBack={onBack}
            nacionalidades={nacionalidades}
            profissoes={profissoes}
            compradores={compradores}
          />
        )
      case 'contatosConjuge':
        return (
          <ContatosComprador
            onNext={onNext}
            onBack={onBack}
            isLast={isLast}
            titulo={t('proposta:cadastro_compradores.steps.contatos.conjuge_titulo')}
            fieldName="conjuge.contato"
            onFinish={(e) => handleOnFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
          />
        )
      case 'dadosMaePai':
        return (
          <DadosMaePai
            onNext={onNext}
            onBack={onBack}
            nacionalidades={nacionalidades}
            profissoes={profissoes}
            compradores={compradores}
          />
        )
      case 'estadoCivilMaePai':
        return (
          <EstadoCivilVinculo
            onNext={onNext}
            onBack={onBack}
            fieldPath="maePai.estadoCivil"
            onFinish={(e) => handleOnFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
            isLast={true}
          />
        )
      case 'contatosMaePai':
        return (
          <ContatosComprador
            onFinish={(e) => handleOnFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
            onNext={onNext}
            onBack={onBack}
            isLast={isLast}
            titulo={t('proposta:cadastro_compradores.steps.contatos.mae_pai_titulo')}
            fieldName="maePai.contato"
          />
        )
      default:
        return null
    }
  }

  return (
    <FormProvider {...form}>
      <StepCompradorLayout steps={steps} current={idx} onNext={onNext} onBack={onBack} onCancel={onCancel}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
